import AnalyticsProperties from './analytics/AnalyticsProperties';
import { Platform } from './Platform';

export type StorageAdapter<T extends Record<string, any> = any> = {
  load: () => Promise<T | undefined>;
  save: (data: T) => Promise<void>;
};

export class PlatformStorage {
  _platform: Platform;
  _loadPromise: null | Promise<{ [key: string]: unknown } | void>;
  _saveHandle: null | number;
  _savePromise: null | Promise<void>;
  _storageKey: null | string;
  persistedData: { [key: string]: unknown };

  /** Total referral count, incremented on each referral in `_populateNotificationPayload`. */
  absoluteSequence: number;

  abTests: { [testId: string]: string };

  /** First entry user properties, set in `sendEntryFinalAnalytics` on first entry. */
  analytics: {
    [key: string]: unknown;
  } & AnalyticsProperties.FirstEntryUserProperties;

  entry: {
    /** Number of entries, incremented after storage load. */
    count: number;

    /** First entry timestamp, set in sendEntryFinalAnalytics on first entry. */
    first: number;

    /** Latest entry timestamp, set in sendEntryFinalAnalytics. */
    last: number;

    /** Latest AD channel entry timestamp, set in sendEntryFinalAnalytics. */
    lastAdEntry: number;

    /** Latest entry from native timestamp, set in sendEntryFinalAnalytics. */
    lastNativeEntry?: number;
  };

  /** Per-feature referral counts, incremented on each referral in `_populateNotificationPayload`. */
  featureAbsoluteSequences: { [feature: string]: number };

  /** Ad analytics state. */
  gcn: {
    /** Number of ads watched. Incremented after each successful ad show. */
    adsWatched: number;
  };

  /** Previous entry's payload, used to track consecutive entries from the same entry point. */
  previousPayload: undefined | { [key: string]: unknown };

  /** The total number of times this user has been "pinched" (i.e. all of a consumable resource has run out). */
  trackPinchAllTimeCount: number | undefined;
  /** The number of times this user has been "pinched" per resource (i.e. all of a given consumable resource has run out). */
  trackPinchAllTimeCounts: Partial<{ [resource: string]: number }> = {};

  [key: string]: any;

  private adapter?: StorageAdapter & { fallbackToPlatformStorage: boolean };

  constructor(platform: Platform) {
    this._platform = platform;
    this._loadPromise = null;
    this._saveHandle = null;
    this._savePromise = null;
    this._storageKey = null;

    this.persistedData = {};

    // N.B only setting the first and second levels of nested property
    // will have the effect of automatically persisting the data
    const storageSchema = {
      abTests: {},
      analytics: {},
      settings: {
        sfxVolume: 0.5,
        bgmVolume: 0.5,
      },
      entry: {
        first: 1545231968498,
        last: 0,
        lastAdEntry: 0,
        count: 0,
      },
      gcn: {
        adsWatched: 0,
      },
      absoluteSequence: 0,
      featureAbsoluteSequences: {},
      previousPayload: undefined,

      trackPinchAllTimeCount: 0,
      trackPinchAllTimeCounts: {},
    };

    if (platform.platformID === 'fb') {
      (storageSchema as any).botSubscriptionViewCount = 0;
    } else if (platform.platformID === 'line') {
      // LINE policy:
      // - max 50 friends can be invited per day
      // - max 1000 invites per day
      // - the day is reset at midnight
      (storageSchema as any).dailyInvites = {
        lastInviteDay: 0,
        count: 0,
        invitees: {},
      };
    }

    this.setStorageSchema(storageSchema);
  }

  /** @returns `true` if storage adapter is configured. */
  hasAdapter(): boolean {
    return !!this.adapter;
  }

  load(): Promise<{ [key: string]: unknown } | void> {
    if (this._loadPromise) {
      return this._loadPromise;
    }

    this._storageKey = this._platform._gameConfig.appID + '.models';

    // Fetch from storage adapter (e.g. Replicant), or from platform storage if adapter is undefined:
    const loadPlatformStorage = () =>
      this._platform
        .getDataAsync([this._storageKey])
        .then((response) => response?.[this._storageKey]);

    this._loadPromise = (
      this.adapter
        ? this.adapter.load().then((dataFromAdapter) => {
            // Only fall back to platform storage if fallback is enabled:
            if (
              !dataFromAdapter &&
              this.adapter.fallbackToPlatformStorage === true
            ) {
              return loadPlatformStorage();
            }

            return dataFromAdapter;
          })
        : loadPlatformStorage()
    )
      .then((data: any) => {
        if (!data) {
          return;
        }

        for (const objectName in data) {
          const loadedObject = data[objectName];

          const objectToPopulate = this.persistedData[objectName];
          if (objectToPopulate === undefined) {
            // object missing from schema
            // it needs to be added directly to platform
            this[objectName] = this.persistedData[objectName] = loadedObject;
            continue;
          }

          // Object belongs to schema, adding it to persisted data is sufficient
          if (typeof loadedObject === 'object') {
            for (const property in loadedObject) {
              objectToPopulate[property] = loadedObject[property];
            }
          } else {
            this.persistedData[objectName] = loadedObject;
          }
        }

        return this.persistedData;
      })
      .catch((e) => {
        console.error('Failed to load platform data:', e);
      });

    return this._loadPromise;
  }

  save(): Promise<void> {
    if (this._loadPromise === null) {
      // Storage needs to load before a first save
      void this.load();
    }

    // If we already have a _saveHandle, return the associated _savePromise
    if (this._saveHandle) {
      return this._savePromise;
    }

    // If there's no existing saveHandle, we create one and a _savePromise
    // wrapper to resolve when the save completes.
    let savePromiseResolve: any = null;
    this._savePromise = new Promise((resolve) => {
      savePromiseResolve = resolve;
    });

    // Wait in case multiple persist calls are made
    this._saveHandle = window.setTimeout(() => {
      this._saveHandle = null;

      const saveObject: any = {};
      saveObject[this._storageKey] = this.persistedData;

      void this._loadPromise
        .then(() => {
          return this.adapter
            ? this.adapter.save(this.persistedData)
            : this._platform.setDataAsync(saveObject);
        })
        .then(() => {
          savePromiseResolve();
        });
    }, 0);

    return this._savePromise;
  }

  _createSaveProxy(
    property: string,
    object: { [key: string]: unknown },
    proxy: { [key: string]: unknown },
  ): void {
    Object.defineProperty(proxy, property, {
      enumerable: true,
      get: () => {
        return object[property];
      },
      set: (value) => {
        // Don't trigger an update if new value equals current value:
        if (
          value === object[property] ||
          JSON.stringify(value) === JSON.stringify(object[property])
        ) {
          return;
        }

        object[property] = value;

        // persisting
        void this.save();
      },
    });
  }

  /**
   * Set custom storage endpoint to be used instead of the default platform storage.
   *
   * **Must be called** before `GCInstant.loadStorage` and `GCInstant.startGameAsync`.
   */
  setStorageAdapter(
    adapter: StorageAdapter,
    opts?: {
      /**
       * If storage adapter returns a falsey value on load, fall back to platform storage.
       *
       * Used for migrating from platform storage to storage adapter.
       *
       * @defaultValue false
       */
      fallbackToPlatformStorage: boolean;
    },
  ): void {
    this.adapter = {
      ...adapter,
      fallbackToPlatformStorage: !!opts?.fallbackToPlatformStorage,
    };
  }

  setStorageSchema(schema: { [key: string]: unknown }): void {
    for (const objectName in schema) {
      const objectSchema = schema[objectName];
      const currentObject = this[objectName] || null;

      if (
        typeof objectSchema === 'object' &&
        Object.keys(objectSchema).length > 0
      ) {
        const propertyData: any = {};
        // attaching property setter onto the platform model for easier access
        const propertyDataSetter = {};
        this[objectName] = propertyDataSetter;
        this.persistedData[objectName] = propertyData;

        for (const property in objectSchema) {
          if (currentObject !== null && currentObject[property] !== undefined) {
            propertyData[property] = currentObject[property];
          } else {
            propertyData[property] = objectSchema[property];
          }
          this._createSaveProxy(property, propertyData, propertyDataSetter);
        }
      } else {
        this._createSaveProxy(objectName, this.persistedData, this);
        this.persistedData[objectName] =
          currentObject !== null ? currentObject : objectSchema;
      }
    }
  }
}

export default PlatformStorage;
